"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { LockKeyholeIcon } from "lucide-react"
import Link from "next/link"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import type { AccountAddressFormData } from "@/api/account-addresses"
import { getAccountAddress } from "@/api/account-addresses"
import { getCartItems, updateCartShippingAddress } from "@/api/cart"
import { submitCheckoutOrder } from "@/api/checkout"
import { BillingDetailsSection } from "@/components/account/complete-purchase/billing-details-section"
import { OrderSummarySection } from "@/components/account/complete-purchase/order-summary-section"
import { PaymentSection } from "@/components/account/complete-purchase/payment-section"
import { Button } from "@/components/ui/button"

const PAYMENT_METHOD_VALUES = [
  "bacs",
  "triibo-payments-pix",
  "triibo-payments-card",
] as const

export const checkoutFormSchema = z
  .object({
    billing: z.object({
      email: z.email(),
      firstName: z.string().trim().min(1),
      lastName: z.string().trim().min(1),
      cpf: z.string().trim().min(1),
      phone: z.string().trim().min(1),
      city: z.string().trim(),
      postcode: z.string().trim().min(1),
      address1: z.string().trim().min(1),
      number: z.string().trim().min(1),
      address2: z.string().trim().optional(),
      district: z.string().trim().min(1),
      state: z.string().trim().min(1),
      notes: z.string().trim().optional(),
    }),
    shipToDifferentAddress: z.boolean(),
    shipping: z.object({
      country: z.string().trim().min(1),
      state: z.string().trim().min(1),
      city: z.string().trim(),
      postcode: z.string().trim(),
      address1: z.string().trim(),
      number: z.string().trim(),
      neighborhood: z.string().trim(),
      address2: z.string().trim().optional(),
    }),
    payment: z.object({
      method: z.enum(PAYMENT_METHOD_VALUES),
      card: z.object({
        holderName: z.string().trim(),
        holderCpf: z.string().trim(),
        holderPhone: z.string().trim(),
        number: z.string().trim(),
        expiry: z.string().trim(),
        cvc: z.string().trim(),
        brand: z.string().trim(),
        installment: z.string().trim(),
      }),
    }),
  })
  .superRefine((value, context) => {
    if (value.shipToDifferentAddress) {
      if (!value.shipping.city.trim()) {
        context.addIssue({
          code: "custom",
          message: "Cidade obrigatoria para endereco de entrega.",
          path: ["shipping", "city"],
        })
      }

      if (!value.shipping.postcode.trim()) {
        context.addIssue({
          code: "custom",
          message: "CEP obrigatorio para endereco de entrega.",
          path: ["shipping", "postcode"],
        })
      }
    }

    if (value.payment.method !== "triibo-payments-card") {
      return
    }

    if (!value.payment.card.holderName.trim()) {
      context.addIssue({
        code: "custom",
        message: "Nome impresso no cartao e obrigatorio.",
        path: ["payment", "card", "holderName"],
      })
    }

    if (!value.payment.card.number.trim()) {
      context.addIssue({
        code: "custom",
        message: "Numero do cartao e obrigatorio.",
        path: ["payment", "card", "number"],
      })
    }

    if (!value.payment.card.holderCpf.trim()) {
      context.addIssue({
        code: "custom",
        message: "CPF do titular e obrigatorio.",
        path: ["payment", "card", "holderCpf"],
      })
    }

    if (!value.payment.card.holderPhone.trim()) {
      context.addIssue({
        code: "custom",
        message: "Telefone do titular e obrigatorio.",
        path: ["payment", "card", "holderPhone"],
      })
    }

    if (!value.payment.card.expiry.trim()) {
      context.addIssue({
        code: "custom",
        message: "Validade e obrigatoria.",
        path: ["payment", "card", "expiry"],
      })
    }

    if (!value.payment.card.cvc.trim()) {
      context.addIssue({
        code: "custom",
        message: "Codigo de seguranca e obrigatorio.",
        path: ["payment", "card", "cvc"],
      })
    }

    if (!value.payment.card.brand.trim()) {
      context.addIssue({
        code: "custom",
        message: "Bandeira do cartao e obrigatoria.",
        path: ["payment", "card", "brand"],
      })
    }

    if (!value.payment.card.installment.trim()) {
      context.addIssue({
        code: "custom",
        message: "Parcelamento e obrigatorio.",
        path: ["payment", "card", "installment"],
      })
    }
  })

export type CheckoutFormValues = z.infer<typeof checkoutFormSchema>

interface CheckoutShippingAddressDefaults {
  country: string
  state: string
  city: string
  postcode: string
}

const createCompletePurchaseDefaultValues = (
  cartShippingAddress: CheckoutShippingAddressDefaults,
  savedBillingAddress?: AccountAddressFormData | null,
  savedShippingAddress?: AccountAddressFormData | null
): CheckoutFormValues => {
  const billing = {
    email: savedBillingAddress?.email || "",
    firstName: savedBillingAddress?.firstName || "",
    lastName: savedBillingAddress?.lastName || "",
    cpf: savedBillingAddress?.cpf || "",
    city: savedBillingAddress?.city || "",
    phone: savedBillingAddress?.phone || "",
    postcode: savedBillingAddress?.zipCode || cartShippingAddress.postcode,
    address1: savedBillingAddress?.street || "",
    number: savedBillingAddress?.number || "",
    address2: savedBillingAddress?.complement || "",
    district: savedBillingAddress?.district || "",
    state: savedBillingAddress?.state || cartShippingAddress.state,
    notes: "",
  }

  return {
    billing,
    shipToDifferentAddress: false,
    shipping: createShippingValues({
      shipToDifferentAddress: false,
      savedShippingAddress,
      billingAddress: billing,
      cartShippingAddress,
    }),
    payment: {
      method: "bacs",
      card: {
        holderName: "",
        holderCpf: "",
        holderPhone: "",
        number: "",
        expiry: "",
        cvc: "",
        brand: "",
        installment: "",
      },
    },
  }
}

const isCheckoutCartEmpty = (
  isCartQuerySuccessful: boolean,
  itemCount: number
): boolean => {
  return isCartQuerySuccessful && itemCount === 0
}

interface CreateShippingValuesParams {
  shipToDifferentAddress: boolean
  savedShippingAddress?: AccountAddressFormData | null
  billingAddress?: CheckoutFormValues["billing"]
  cartShippingAddress?: CheckoutShippingAddressDefaults
}

const createShippingValues = ({
  shipToDifferentAddress,
  savedShippingAddress,
  billingAddress,
  cartShippingAddress,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Intentional priority fallback chain for address sources
}: CreateShippingValuesParams): CheckoutFormValues["shipping"] => {
  if (shipToDifferentAddress && savedShippingAddress) {
    return {
      country: "BR",
      state: savedShippingAddress.state || cartShippingAddress?.state || "SP",
      city: savedShippingAddress.city || cartShippingAddress?.city || "",
      postcode:
        savedShippingAddress.zipCode || cartShippingAddress?.postcode || "",
      address1: savedShippingAddress.street || "",
      number: savedShippingAddress.number || "",
      neighborhood: savedShippingAddress.district || "",
      address2: savedShippingAddress.complement || "",
    }
  }

  if (billingAddress) {
    return {
      country: "BR",
      state: billingAddress.state || cartShippingAddress?.state || "SP",
      city: cartShippingAddress?.city || "",
      postcode: billingAddress.postcode || cartShippingAddress?.postcode || "",
      address1: billingAddress.address1 || "",
      number: billingAddress.number || "",
      neighborhood: billingAddress.district || "",
      address2: billingAddress.address2 || "",
    }
  }

  return {
    country: cartShippingAddress?.country || "BR",
    state: cartShippingAddress?.state || "SP",
    city: cartShippingAddress?.city || "",
    postcode: cartShippingAddress?.postcode || "",
    address1: "",
    number: "",
    neighborhood: "",
    address2: "",
  }
}

export default function Page() {
  const queryClient = useQueryClient()

  const cartQuery = useQuery({
    queryKey: ["cart", "items"],
    queryFn: getCartItems,
    retry: false,
  })

  const billingAddressQuery = useQuery({
    queryKey: ["account", "address", "billing"],
    queryFn: () => getAccountAddress("billing"),
    retry: false,
  })

  const shippingAddressQuery = useQuery({
    queryKey: ["account", "address", "shipping"],
    queryFn: () => getAccountAddress("shipping"),
    retry: false,
  })

  const checkoutShippingAddress = {
    country: cartQuery.data?.shippingAddress?.country ?? "BR",
    state: cartQuery.data?.shippingAddress?.state ?? "SP",
    city: cartQuery.data?.shippingAddress?.city ?? "",
    postcode: cartQuery.data?.shippingAddress?.postcode ?? "",
  }

  const savedBillingAddress =
    billingAddressQuery.data?.ok && billingAddressQuery.data.address
      ? billingAddressQuery.data.address
      : null

  const savedShippingAddress =
    shippingAddressQuery.data?.ok && shippingAddressQuery.data.address
      ? shippingAddressQuery.data.address
      : null

  const completePurchaseValues = createCompletePurchaseDefaultValues(
    checkoutShippingAddress,
    savedBillingAddress,
    savedShippingAddress
  )

  const form = useForm<CheckoutFormValues>({
    resolver: zodResolver(checkoutFormSchema),
    mode: "onChange",
    values: completePurchaseValues,
    resetOptions: {
      keepDirtyValues: true,
    },
  })

  const handleShipToDifferentAddressChange = (checked: boolean) => {
    const cartShippingAddressDefaults = {
      country: cartQuery.data?.shippingAddress?.country || "BR",
      state: cartQuery.data?.shippingAddress?.state || "SP",
      city: cartQuery.data?.shippingAddress?.city || "",
      postcode: cartQuery.data?.shippingAddress?.postcode || "",
    }

    form.setValue(
      "shipping",
      createShippingValues({
        shipToDifferentAddress: checked,
        savedShippingAddress,
        billingAddress: form.getValues("billing"),
        cartShippingAddress: cartShippingAddressDefaults,
      })
    )
  }

  const updateShippingAddressMutation = useMutation({
    mutationFn: updateCartShippingAddress,
    onMutate: () => {
      const toastId = toast.loading("Atualizando endereco de entrega...")
      return { toastId }
    },
    onError: (_error, _variables, context) => {
      toast.error("Nao foi possivel atualizar o endereco de entrega.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _variables, context) => {
      if (!response.ok) {
        toast.error(
          response.message?.trim() ||
            "Nao foi possivel atualizar o endereco de entrega.",
          {
            id: context?.toastId,
          }
        )

        return
      }

      toast.success(response.message?.trim() || "Endereco atualizado.", {
        id: context?.toastId,
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
      await queryClient.invalidateQueries({
        queryKey: ["checkout", "order-review"],
      })
    },
  })

  const submitCheckoutMutation = useMutation({
    mutationFn: submitCheckoutOrder,
    onMutate: () => {
      const toastId = toast.loading("Processando pagamento...")
      return { toastId }
    },
    onError: (_error, _variables, context) => {
      toast.error("Nao foi possivel finalizar compra.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _variables, context) => {
      if (!response.ok) {
        toast.error(
          response.message?.trim() || "Nao foi possivel finalizar compra.",
          {
            id: context?.toastId,
          }
        )
        return
      }

      if (!response.redirectUrl) {
        toast.error("Nao foi possivel finalizar compra.", {
          id: context?.toastId,
        })
        return
      }

      toast.success("Pedido criado. Redirecionando...", {
        id: context?.toastId,
      })
      window.location.assign(response.redirectUrl)
    },
  })

  const itemCount = cartQuery.data?.items.length ?? 0
  const isCartEmpty = isCheckoutCartEmpty(cartQuery.isSuccess, itemCount)
  const outOfStockItems =
    cartQuery.data?.items.filter((item) => item.stockStatus === "outofstock") ??
    []
  const hasOutOfStockItems = outOfStockItems.length > 0

  useEffect(() => {
    if (!isCartEmpty) {
      return
    }

    toast.error("Seu carrinho esta vazio.")
    window.location.assign("/")
  }, [isCartEmpty])

  if (isCartEmpty || !cartQuery.data) {
    return null
  }

  if (hasOutOfStockItems) {
    return (
      <main className='min-h-screen py-8 sm:py-12'>
        <div className='mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8'>
          <header className='mb-6 rounded-2xl border border-zinc-200 bg-white/80 p-5 shadow-xs backdrop-blur sm:p-6'>
            <p className='font-semibold text-muted-foreground text-xs uppercase tracking-[0.16em]'>
              Checkout
            </p>
            <div className='mt-2 flex flex-wrap items-end justify-between gap-3'>
              <h1 className='font-semibold text-3xl text-zinc-900 tracking-tight sm:text-4xl'>
                Finalizar compra
              </h1>
              <div className='inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 font-medium text-sm text-zinc-700'>
                <LockKeyholeIcon aria-hidden='true' size={14} />
                Ambiente seguro
              </div>
            </div>
          </header>

          <section className='space-y-4 rounded-2xl border border-destructive/40 bg-destructive/5 p-5 sm:p-6'>
            <h2 className='font-semibold text-destructive text-lg'>
              Existem produtos sem estoque no seu carrinho
            </h2>
            <p className='text-sm text-zinc-700'>
              Remova os produtos destacados como sem estoque no carrinho para
              continuar o checkout.
            </p>

            <ul className='space-y-2'>
              {cartQuery.data.items.map((item) => {
                const isOutOfStock = item.stockStatus === "outofstock"

                return (
                  <li
                    className={
                      isOutOfStock
                        ? "rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2"
                        : "rounded-lg border border-zinc-200 bg-white px-3 py-2"
                    }
                    key={item.key}
                  >
                    <div className='flex items-center justify-between gap-3'>
                      <span className='text-sm text-zinc-900'>{item.name}</span>
                      {isOutOfStock ? (
                        <span className='rounded-full bg-destructive/15 px-2 py-1 font-semibold text-[11px] text-destructive uppercase tracking-wide'>
                          Sem estoque
                        </span>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>

            <Button asChild className='w-full sm:w-auto'>
              <Link href='/carrinho'>Voltar para o carrinho</Link>
            </Button>
          </section>
        </div>
      </main>
    )
  }

  return (
    <main className='min-h-screen py-8 sm:py-12'>
      <div className='mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8'>
        <header className='mb-6 rounded-2xl border border-zinc-200 bg-white/80 p-5 shadow-xs backdrop-blur sm:p-6'>
          <p className='font-semibold text-muted-foreground text-xs uppercase tracking-[0.16em]'>
            Checkout
          </p>
          <div className='mt-2 flex flex-wrap items-end justify-between gap-3'>
            <h1 className='font-semibold text-3xl text-zinc-900 tracking-tight sm:text-4xl'>
              Finalizar compra
            </h1>
            <div className='inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 font-medium text-sm text-zinc-700'>
              <LockKeyholeIcon aria-hidden='true' size={14} />
              Ambiente seguro
            </div>
          </div>
        </header>

        <div className='grid items-start gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]'>
          <BillingDetailsSection
            form={form}
            onShipToDifferentAddressChange={handleShipToDifferentAddressChange}
            updateShippingAddressMutation={updateShippingAddressMutation}
          />

          <aside className='space-y-5 lg:sticky lg:top-6'>
            <OrderSummarySection />

            <PaymentSection
              form={form}
              submitCheckoutMutation={submitCheckoutMutation}
            />
          </aside>
        </div>
      </div>
    </main>
  )
}
